import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
    signal,
} from "@angular/core";
import { UserprefService } from "../../services/userpref.service";
import { Item } from "../../pojo/Item";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AvailCheck } from "../../pojo/AvailabilityCheck";
import conflictTypes = AvailCheck.conflictTypes;

export type Conflict = SpaceConflict | ResourceConflict;
export interface SpaceConflict extends ConflictDefaults {}
export interface ResourceConflict extends ConflictDefaults {}
export interface ConflictDefaults {
    candidateDate: Date;
    candidateId: number;
    conflictEnd: Date;
    conflictHash: string;
    conflictName: string;
    conflictId: number;
    conflictOverride: ConflictOverride;
    conflictRelation: "direct" | "also";
    conflictShare: boolean;
    conflictStart: Date;
    conflictType: conflictTypes;
    date: string;
    isReal: boolean;
    itemId: number;
    itemName: string;
    itemTypeId: Item.Id;
    occUUID: string;
    uuid: string;
}

export interface ConflictOverride {
    blackout: boolean;
    block: boolean;
    closed: boolean;
    share: boolean;
}

@TypeManagerDecorator("s25-ng-conflicts")
@Component({
    selector: "s25-ng-conflicts",
    template: `
        @if (collapsible) {
            <s25-simple-collapse
                [headerText]="'Conflict Details'"
                [defaultCollapsed]="true"
                [attr.data-real]="isReal"
                (opened)="collapseOpened()"
            >
                @if (onOpen && !model) {
                    <s25-ng-loading-inline-static />
                } @else {
                    <ng-container [ngTemplateOutlet]="info" />
                }
            </s25-simple-collapse>
        } @else {
            <ng-container [ngTemplateOutlet]="info" />
        }

        <ng-template #info>
            <div>
                <div
                    *ngIf="isInit"
                    class="cn-alert cn-alert--info cn-alert--{{ isReal ? 'danger' : 'notRealConflict' }}"
                >
                    <div
                        class="cn-alert__icon cn-icon {{ isReal ? 'cn-alert__icon--red' : 'cn-alert__icon--blue' }}"
                        name="alert--info"
                    >
                        <svg class="cn-svg-icon" role="img">
                            <title>Conflicts Warning</title>
                            <use
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                xlink:href="./resources/typescript/assets/css-compiled/images/sprite.svg#warnings--yield"
                            ></use>
                        </svg>
                    </div>
                    <div class="cn-alert__label">
                        <div class="c-red ngBold c-margin-bottom--half">Conflicts on:</div>
                        @if (isRequestConflict()) {
                            {{ model?.[0]?.itemName ?? "This location  " }} has been requested on the dates below:
                        }
                        <p
                            class="ngObjOccConflict c-margin-top--single"
                            *ngFor="let conflict of conflicts | slice: 0 : 5; let last = last"
                        >
                            {{ conflict.candidateDate | dateFormat: dateFormat }}{{ last ? "" : ", " }}
                        </p>
                        <div *ngFor="let date of candidateDates" class="rose-form--conflictsRow c-margin-top--single">
                            <p class="rose-form--conflictsDate c-padding-bottom--quarter">
                                {{ date.candidateDate | dateFormat: dateFormat }}
                            </p>
                            <div>
                                <ul>
                                    <li *ngFor="let conflict of date.conflicts">
                                        <span class="request-conflict-wrapper">
                                            @if (conflict.conflictType === "req_rsrv") {
                                                <s25-ng-icon
                                                    [type]="'queue'"
                                                    [label]="'Request Conflict'"
                                                ></s25-ng-icon>
                                            }
                                            {{ conflict.itemName }}
                                        </span>
                                        <div [ngSwitch]="conflict.conflictType">
                                            <span *ngSwitchCase="'blackout'">Blackout: {{ conflict.conflictName }}</span
                                            ><span *ngIf="conflict.conflictOverride?.blackout"> (Overridden)</span>
                                            <span *ngSwitchCase="'hours'">Closed: {{ conflict.conflictName }}</span
                                            ><span *ngIf="conflict.conflictOverride?.closed"> (Overridden)</span>
                                            <span *ngSwitchCase="'block'">
                                                Blocked:
                                                <s25-item-event [modelBean]="generateItem(conflict)"></s25-item-event>
                                                <span *ngIf="conflict.conflictOverride?.block"> (Overridden)</span>
                                            </span>
                                            <span *ngSwitchCase="'rsrv'">
                                                <s25-item-event [modelBean]="generateItem(conflict)"></s25-item-event>
                                                <span
                                                    *ngIf="conflict.conflictShare && conflict.conflictOverride?.share"
                                                >
                                                    (Shared)</span
                                                >
                                            </span>
                                            <span *ngSwitchDefault>{{ conflict.conflictName }}</span>
                                        </div>
                                        <span
                                            >{{ conflict.conflictStart | dateFormat: timeFormat }} -
                                            {{ conflict.conflictEnd | dateFormat: timeFormat }}</span
                                        >
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    styles: `
        ::ng-deep s25-ng-conflicts s25-simple-collapse .c-sectionHead {
            background: white !important;
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
        }
        ::ng-deep s25-ng-conflicts s25-simple-collapse .c-sectionHead::before {
            content: "";
            border-left: 0.25em solid var(--conflict-color);
        }

        ::ng-deep s25-ng-conflicts s25-simple-collapse[data-real="false"] .c-sectionHead::before {
            border-left: 0.25em solid var(--conflict-not-real-color);
        }

        ::ng-deep s25-ng-conflicts s25-simple-collapse .content-wrapper {
            font-size: 0.9em;
            padding: 1rem 0;
        }

        s25-simple-collapse .cn-alert {
            margin: 0;
        }

        ::ng-deep .request-conflict-wrapper {
            display: inline-flex;
            align-items: center;
            flex-wrap: nowrap;
        }

        .rose-form--conflictsRow li {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
            margin-bottom: 11px;
        }

        .rose-form--conflictsRow li > span {
            margin-left: 5px;
        }

        .cn-alert {
            align-items: flex-start;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25ConflictsComponent implements OnInit {
    @Input() model: Conflict[];
    @Input() collapsible: boolean = false;
    @Input() onOpen?: () => Promise<typeof this.model>;

    itemHash: { [key: string]: { itemTypeId: Item.Id; itemId: number; itemName: string } } = {};
    candidateDates: { candidateDate: Date; conflicts: Conflict[] }[] = [];
    isReal: boolean;
    dateFormat: string;
    timeFormat: string;
    isInit: boolean;

    isRequestConflict = signal<boolean>(false);

    constructor(private changeDetector: ChangeDetectorRef) {}

    async ngOnInit() {
        this.init();
        const [dateFormat, timeFormat] = await Promise.all([
            UserprefService.getS25Dateformat(),
            UserprefService.getS25Timeformat(),
        ]);
        this.dateFormat = dateFormat;
        this.timeFormat = timeFormat;
        this.isInit = true;
        this.changeDetector.detectChanges();
    }

    init() {
        const candidateDateHash: { [key: string]: any } = {};
        this.checkRequestConflict();
        for (let conflict of this.model || []) {
            this.isReal = (this.isReal || conflict.isReal) && !this.isRequestConflict();
            const key = Number(conflict.candidateDate);
            if (!candidateDateHash[key]) {
                const obj: any = { candidateDate: conflict.candidateDate, conflicts: [] };
                candidateDateHash[key] = obj;
                this.candidateDates.push(obj);
            }
            const obj = candidateDateHash[key];
            obj.conflicts.push(conflict);
        }
    }

    generateItem(conflict: Conflict) {
        const key = conflict.conflictId + "&" + conflict.conflictName;
        if (!this.itemHash[key]) {
            this.itemHash[key] = { itemTypeId: 1, itemId: conflict.conflictId, itemName: conflict.conflictName };
        }
        return this.itemHash[key];
    }

    async collapseOpened() {
        if (!this.onOpen || !!this.model) return;
        this.model = await this.onOpen();
        this.init();
        this.changeDetector.detectChanges();
    }

    checkRequestConflict() {
        const hasRequestConflicts = this.model.some((c: ConflictDefaults) =>
            ["req_rsrv", "req_block"].includes(c.conflictType),
        );
        const hasRsrvBlockConflicts = this.model.some((c: ConflictDefaults) =>
            ["rsrv", "block"].includes(c.conflictType),
        );

        this.isRequestConflict.set(hasRequestConflicts && !hasRsrvBlockConflicts);
    }
}
